import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ScheduleIcon from "@material-ui/icons/Schedule";
import ClassNames from "classnames";
import dayjs from "dayjs";
import utc from "dayjs-plugin-utc";
import { Fragment, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";


import AcknowledgementPassword from "../../components/AcknowledgementPassword";
import AlertDialog from "../../components/AlertDialog";
import Button from "../../components/Button";
import NotesSidebar from "../../components/NotesSidebar";
import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import SessionReminderCard from "../../components/SessionReminderCard";
import Stepper from "../../components/Stepper";
import SubjectListWithTarget from "../../components/SubjectListWithTarget";
import { Framework, Method, SessionStatus } from "../../config/Config";
import { IsSessionCoach, IsSessionCoachee } from "../../config/Roles";
import { useNotification } from "../../context/notification";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import styles from "./SessionsCoachingSummary.module.scss";

import { useTranslation } from "react-i18next";
import BackButton from "../../components/BackButton";
import SessionBasicInfo from "../../components/SessionBasicInfo";
import { sessionPath } from "../../config/GetPagePath";
import GetSessionData from "../../services/getSessionData";

dayjs.extend(utc);

export function SessionsCoachingSummary() {
  const history = useHistory();
  const { id } = useParams();
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [subjectList, setSubjectList] = useState([]);
  const [growFormDetails, setGrowFormDetails] = useState({});
  const [sessionData, setSessionData] = useState([]);
  const [sessionReviewDate, setSessionReviewDate] = useState("");
  const [password, setPassword] = useState("");
  const [sessionCoach, setSessionCoach] = useState(null);
  const [sessionCoachee, setSessionCoachee] = useState(null);
  const [isScheduled, setIsScheduled] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [clockRunCheck, setClockRunCheck] = useState(false);
  const [open, setOpen] = useState(false);
  const [acknowledgement, setAcknowledgement] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [hideProjectTargetKpi, sethideProjectTargetKpi] = useState(false);
  const [hideCurrentMetrics, sethideCurrentMetricsi] = useState(false);
  const [hideTargetMetrics, sethhideTargetMetrics] = useState(false);

  // fetch growFormDetails
  async function fetchGrowFormDetails() {
    const response = await apiRequest.get(
      `/api/getGrowformDetails?session_id=${id}`
    );

    if (response.status === 200) {
      const data = response.data.data;

      setSubjectList(data.subject);
      setGrowFormDetails(data.grow_form_static_data);
    } else {
      addNotification(
        t("error.couldNotGetGrowFormDetailsFromSessionId") +
        ` #${id}. (Status code: ${response.status})`,
        "error"
      );
    }
  }

  useEffect(() => {
    // fetch session details

    async function fetchSessionData() {
      setIsLoading(true);
      const sessionAPIData = await GetSessionData(id);
      if (sessionAPIData) {
        setSessionData(sessionAPIData.session);
      } else {
        addNotification(
          `Could not get session details from id #${id}. (Status code: ${sessionAPIData.status})`,
          "error"
        );
      }
      fetchGrowFormDetails(); // fetch growFormDetails afterwards
      setIsLoading(false);
    }

    fetchSessionData();
    // fetch session review date
    apiRequest
      .get(`/api/getSessionReviewDate?session_id=${id}`)
      .then(res => {
        if (res && res.data.data) {
          let newDate = res.data.data.current_review_date[0].review_datetime;
          setSessionReviewDate(new Date(newDate));
        }
      })
      .finally(() => { });
  }, []);

  const isSpecialSession = sessionData?.session_type_id === 3;

  useEffect(() => {
    console.log(sessionData, "sessions data");
    if (sessionData && sessionData.session_type_id === 2) {
      sethideProjectTargetKpi(true);
      sethideCurrentMetricsi(true);
      sethhideTargetMetrics(true);
      // console.log(response.session_type_id,'response')
    } else {
      sethideProjectTargetKpi(false);
      sethideCurrentMetricsi(false);
      sethhideTargetMetrics(false);
    }
  }, [sessionData]);
  useEffect(() => {
    if (acknowledgement) handleConfirm();
  }, [acknowledgement]);

  const handleConfirm = () => {
    setIsLoading(true);
    apiRequest
      .post(`/api/endCoachingSession`, {
        session_id: sessionData["session_id"],
        acknowledged_by_uid: getLocalValue("uid"),
        username: getLocalValue("u_username"), // doubt: what should this be?
        password: password,
        duration: sessionDuration,
        framework: sessionData["framework"],
      })
      .then(resp => {
        setIsLoading(false);
        if (resp.status === 200) {
          if (resp.data.hasOwnProperty("msg")) {
            // invalid password response
            addNotification(resp.data.msg, "error");
            setPasswordError(true);
            setAcknowledgement(false);
          } else if (
            resp.data.hasOwnProperty("data") &&
            resp.data.data.hasOwnProperty("session_id")
          ) {
            // success response
            addNotification("Session successfully ended.", "success");
            history.push(sessionPath(true));
          } else {
            setAcknowledgement(false);
            addNotification(
              "We didn't get a correct response from the server. Please make sure your password is correct and try again. Otherwise, contact the system administrator.",
              "error"
            );
          }
        }
      })
      .catch(err => {
        setIsLoading(false);
        setAcknowledgement(false);
      });
  };

  const handleAcknowledgementOpen = () => {
    if (password) {
      setOpen(true);
    } else {
      addNotification(t("coachingSummary.pleaseEnterPassword"), "error");
    }
  };

  const handleAcknowledgementClose = value => {
    setOpen(false);
    setAcknowledgement(value);
  };

  const getSessionValue = field => {
    return sessionData ? sessionData[field] : undefined;
  };

  // get current session duration for the stepper
  const getTotalTime = time => {
    setSessionDuration(time);
  };

  useEffect(() => {
    setSessionCoach(IsSessionCoach(getSessionValue("coach_id")));
    setSessionCoachee(IsSessionCoachee(getSessionValue("coachee_id")));
    setIsScheduled(getSessionValue("status") === SessionStatus.SCHEDULED);
    setTimeout(function () {
      setClockRunCheck(true);
    }, 1000);
  }, [sessionData]);

  return (
    <DashboardLayout
      appBarContent={
        clockRunCheck && (
          <Stepper
            isPDFlow={sessionData["framework"] === Framework.PD}
            phase={t("header.stepper.do")}
            phaseLabel="Do"
            sessionId={id}
            clockRun={isScheduled}
            getTotalTimeSpent={getTotalTime}
          />
        )
      }
      rightSidebarContent={
        <NotesSidebar
          sessionID={id}
          isCoach={sessionData["coach_id"] === parseInt(getLocalValue("uid"))}
          accessible={true}
        />
      }
    >
      <div className={styles.pageWrapper}>
        <PageContent indent>
          <Section>
            <div className={styles.detailsContainer}>
              <BackButton text={t("common.backToSession")} />
              <SectionTitle>
                {t("coachingSummary.coachingSummary")}
              </SectionTitle>
              <div className={styles.sessionDetailsBlock}>
                <SessionBasicInfo sessionData={sessionData} />
                <div className={styles.row}>
                  <div className={styles.rowLeft}>
                    {t("coachingSummary.coachingDate")}
                  </div>
                  <div className={styles.rowRight}>
                    <span className={styles.rowIcon}>
                      <CalendarTodayIcon />
                    </span>
                    <span style={{ padding: "0 12px 0 4px" }}>
                      {/* Time format: 9 Jan 2020 */}
                      {dayjs(sessionData["session_datetime"])
                        .add(dayjs().utcOffset(), "minutes")
                        .format("D MMM YYYY")}
                    </span>
                    <span className={styles.rowIcon}>
                      <ScheduleIcon />
                    </span>
                    <span style={{ padding: "0 12px 0 4px" }}>
                      {/* Time format: 8:12 PM*/}
                      {dayjs(sessionData["session_datetime"])
                        .add(dayjs().utcOffset(), "minutes")
                        .format("h:mm A")}
                    </span>
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.rowLeft}>
                    {" "}
                    {t("coachingSummary.mode")}
                  </div>
                  <div className={styles.rowRight}>
                    {Method[sessionData["method"]]}
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.rowLeft}>
                    {t("coachingSummary.typeOfSession")}
                  </div>
                  <div className={styles.rowRight}>
                    {sessionData["session_type"]}
                  </div>
                </div>
              </div>
              <div
                className={ClassNames(
                  styles.sessionGrowFormBlock,
                  styles.borderTop
                )}
              >
                {isSpecialSession && (
                  <>
                    <SubjectListWithTarget
                      hideProjectTargetKpi={hideProjectTargetKpi}
                      hideCurrentMetrics={hideCurrentMetrics}
                      hideTargetMetrics={hideTargetMetrics}
                      list={
                        subjectList && subjectList.length > 0 ? subjectList : []
                      }
                      hasSkillSelect
                    />
                    {Object.keys(growFormDetails)
                      .filter(key => key.startsWith("question"))
                      .map((key, index) => (
                        <div className={styles.row} key={key}>
                          <div className={styles.labelNumber}>{index + 1}.</div>
                          <div className={styles.rowLeft}>
                            <div className={styles.labelTitle}>
                              {growFormDetails[key]}
                            </div>
                          </div>
                          <div className={styles.rowRight}>
                            <div className={styles.content}>
                              <p>{growFormDetails[`answer${index + 1}`]}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </>
                )}
                {!isSpecialSession && (
                  <>
                    <div className={styles.row}>
                      <div className={styles.rowLeft}>
                        <div className={styles.labelTitle}>
                          {t("coachingSummary.goals")}
                        </div>
                      </div>
                      <div className={styles.rowRight}>
                        <div className={styles.content}>
                          <p>{growFormDetails["goal"]}</p>
                        </div>
                      </div>
                    </div>
                    <div className={styles.row}>
                      <div className={styles.rowLeft}>
                        <div className={styles.labelTitle}>
                          {t("coachingSummary.reality")}
                        </div>
                      </div>
                      <div className={styles.rowRight}>
                        <SubjectListWithTarget
                          hideProjectTargetKpi={hideProjectTargetKpi}
                          hideCurrentMetrics={hideCurrentMetrics}
                          hideTargetMetrics={hideTargetMetrics}
                          list={
                            subjectList && subjectList.length > 0
                              ? subjectList
                              : []
                          }
                          hasSkillSelect
                        />
                        <div className={styles.content}>
                          <p>{growFormDetails["reality_optional"]}</p>
                        </div>
                      </div>
                    </div>
                    <div className={styles.row}>
                      <div className={styles.rowLeft}>
                        <div className={styles.labelTitle}>
                          {t("coachingSummary.options")}
                        </div>
                      </div>
                      <div className={styles.rowRight}>
                        <div className={styles.contentContainer}>
                          <div className={styles.content}>
                            <p>{growFormDetails["opportunity"]}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={ClassNames(styles.row)}>
                      <div className={styles.rowLeft}>
                        <div className={styles.labelTitle}>
                          {t("coachingSummary.wayForward")}
                        </div>
                      </div>
                      <div className={styles.rowRight}>
                        <div className={styles.contentContainer}>
                          <div className={styles.content}>
                            <p>{growFormDetails["way_forward"]}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {sessionData["framework"] !== Framework.PD && (
                <SessionReminderCard
                  header={t("coachingSummary.yourReviewSessionWillBe")}
                  date={dayjs(sessionReviewDate).format("D MMM YYYY")}
                  time={dayjs(sessionReviewDate).format("h:mm A")}
                  notes={t("coachingSummary.progressionCheckpoints")}
                />
              )}
              {sessionCoachee && isScheduled && (
                <Fragment>
                  <AcknowledgementPassword
                    label={t("coachingSummary.acknowledgement")}
                    error={passwordError}
                    setPassword={setPassword}
                  />
                  <div className={styles.confirmButtonWrapper}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={handleAcknowledgementOpen}
                      isloading={isLoading ? 1 : 0}
                    >
                      {t("coachingSummary.endCoaching")}
                    </Button>
                  </div>
                </Fragment>
              )}
            </div>
          </Section>
        </PageContent>
        <AlertDialog
          open={open}
          onClose={handleAcknowledgementClose}
          title={t("coachingSummary.acknowledgement")}
          body={t("coachingSummary.byAcknowledgingthisGrow")}
          footer={
            <Fragment>
              <Button
                onClick={() => {
                  handleAcknowledgementClose(false);
                }}
              >
                {t("coachingSummary.cancel")}
              </Button>
              <Button
                onClick={() => {
                  handleAcknowledgementClose(true);
                }}
                color="primary"
                variant="contained"
              >
                {t("coachingSummary.agree")}
              </Button>
            </Fragment>
          }
        />
      </div>
    </DashboardLayout>
  );
}
